import { KATALOG, LEER, mitNeuenNamen, type Schluessel, type Wert } from '../platform/einstellungen';
import type { Store } from './port';
import { loadSettings, saveSetting } from './settings';

/**
 * Was ein gescannter Einstellungscode gesetzt hat — die oberste Quelle der Rangfolge.
 *
 * **Warum eine eigene Ablage.** Der Nutzer hat am 2026-09-13 entschieden, dass ein gescannter
 * Code auch die Geräteverwaltung schlägt. Schriebe die App die Werte einfach in die
 * iPad-Einstellungen, gewänne das MDM sie beim nächsten Lesen zurück (`mische`). Also liegen
 * sie getrennt, in derselben Tabelle wie die übrigen App-Einstellungen, aber unter einem
 * eigenen Vorsatz.
 *
 * ⚠️ **Der Vorsatz ist Teil des Namens, nicht eine zweite Tabelle.** `ClientSettingValue` ist
 * eine Namen-Wert-Ablage; ein Name mehr kostet nichts, eine Tabelle mehr kostet eine
 * Migration. Wer die Werte lesen will, filtert auf den Vorsatz.
 */

const VORSATZ = 'qr.';

/**
 * Wie ein ausdrücklich geleerter Wert in der Ablage steht (seit 2026-10-01, s.
 * `platform/einstellungen.ts::LEER`). `''` ist schon vergeben — es heisst „zurückgenommen", und
 * so stehen es bestehende Geräte in ihrer Ablage. Ein Zeichen, das niemand tippt.
 */
const LEER_IN_DER_ABLAGE = '\u2400';

/** Die gescannten Werte, in den Namen des Katalogs. Unbekannte Namen fallen weg. */
export async function leseQrUeberschreibungen(
  store: Store,
  clientId: string,
): Promise<Partial<Record<Schluessel, unknown>>> {
  const alle = await loadSettings(store, clientId);
  const ergebnis: Partial<Record<Schluessel, unknown>> = {};

  // Ohne Vorsatz, und in die heutigen Namen übersetzt: ein vor dem 2026-10-01 gescannter Code
  // liegt noch unter `qr.server_login` und gilt jetzt für `classic_login`.
  const gescannt: Record<string, string> = {};
  for (const [name, roh] of Object.entries(alle)) {
    if (name.startsWith(VORSATZ)) gescannt[name.slice(VORSATZ.length)] = roh;
  }

  for (const [schluessel, roh] of Object.entries(mitNeuenNamen(gescannt)) as [Schluessel, string][]) {
    if (roh === LEER_IN_DER_ABLAGE) {
      ergebnis[schluessel] = LEER;
      continue;
    }
    // ⚠️ **Leer heisst zurückgenommen, für jeden Typ.** `verwirfQrUeberschreibungen` schreibt
    // eine leere Zeichenkette; ohne diese Zeile würde daraus bei einem Schalter ein
    // ausdrückliches `false`, und das Gerät bliebe der Verwaltung entzogen — nur eben mit
    // abgeschaltetem Schalter statt mit dem gescannten Wert.
    if (roh === '') continue;
    const eintrag = KATALOG[schluessel];
    // In der Ablage steht alles als Text — `true` wäre sonst die Zeichenkette `'true'` und
    // fiele bei `pruefe` eines Schalters durch.
    ergebnis[schluessel] =
      typeof eintrag.vorgabe === 'boolean'
        ? roh === 'true'
        : typeof eintrag.vorgabe === 'number'
          ? Number(roh)
          : roh;
  }
  return ergebnis;
}

/**
 * Die gescannten Werte ablegen.
 *
 * Geschrieben wird **nur**, was der Code nennt: alles andere bleibt, wie es war, und fällt
 * weiter auf MDM, iPad-Einstellungen und Vorgabe zurück. Ein Code, der nur die Serveradresse
 * trägt, darf nicht die Sprache zurücksetzen.
 *
 * `LEER` leert einen Wert ausdrücklich; `null` (oder `''`) nimmt die Überschreibung für diesen
 * Schlüssel zurück — dann gelten wieder MDM, iPad-Seite, Vorgabe.
 */
export async function schreibeQrUeberschreibungen(
  store: Store,
  clientId: string,
  werte: Partial<Record<Schluessel, Wert | typeof LEER | null>>,
): Promise<void> {
  for (const [schluessel, wert] of Object.entries(werte) as [Schluessel, Wert | typeof LEER | null][]) {
    const text = wert === LEER ? LEER_IN_DER_ABLAGE : wert === null ? '' : String(wert);
    await saveSetting(store, clientId, VORSATZ + schluessel, text);
  }
}

/**
 * Alle gescannten Werte wieder zurücknehmen — damit ein Gerät zur Verwaltung zurückfindet.
 *
 * ⚠️ **Ohne diesen Weg wäre ein einmal gescanntes iPad dauerhaft der Verwaltung entzogen.**
 * Das MDM kann seine Werte zwar weiter schicken, käme aber nie wieder zum Zug.
 */
export async function verwirfQrUeberschreibungen(store: Store, clientId: string): Promise<void> {
  const alle = await loadSettings(store, clientId);
  for (const name of Object.keys(alle)) {
    if (name.startsWith(VORSATZ)) await saveSetting(store, clientId, name, '');
  }
}
